import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BulkArchiveJobsRequestDto,
  JobDto,
  JobRequestDto,
  JobTemplateSettingsDto,
  JobWorkflowSettingsDto,
} from 'src/api/FiretrolApiClient';
import { ctcApiClient, jobApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { queryKeys } from 'src/api/queries/queryKeys';

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: JobRequestDto) => jobApiClient.create(data),
    meta: {
      successMessageFn: (job) => `${(job as JobDto).name} Created!`,
      suppressErrorToast: true,
    },
    onSettled: () => invalidateFor('job.create', qc),
  });
}

export function useUpdateJobDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, data }: { jobId: number; data: JobRequestDto }) =>
      jobApiClient.updateJobDetails(jobId, data),
    meta: { successMessage: 'Job details updated!', suppressErrorToast: true },
    onSuccess: (job) => qc.setQueryData(queryKeys.jobs.detail(job.id), job),
    onSettled: (_data, _err, { jobId }) =>
      invalidateFor('job.update', qc, { jobId }),
  });
}

export function useUpdateJobTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      data,
    }: {
      jobId: number;
      data: JobTemplateSettingsDto;
    }) => jobApiClient.updateJobTemplates(jobId, data),
    meta: { successMessage: 'Template Settings Updated!' },
    onSuccess: (job) => qc.setQueryData(queryKeys.jobs.detail(job.id), job),
    onSettled: (_data, _err, { jobId }) =>
      invalidateFor('job.update', qc, { jobId }),
  });
}

export function useUpdateJobWorkflows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      data,
    }: {
      jobId: number;
      divisionId?: number;
      data: JobWorkflowSettingsDto;
    }) => jobApiClient.updateWorkflows(jobId, data),
    meta: { successMessage: 'Workflow settings updated!' },
    onSuccess: (job) => qc.setQueryData(queryKeys.jobs.detail(job.id), job),
    onSettled: (_data, _err, { jobId, divisionId }) =>
      invalidateFor('job.workflows.update', qc, { jobId, divisionId }),
  });
}

export function useArchiveJobs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkArchiveJobsRequestDto) =>
      jobApiClient.bulkArchiveJobs(data),
    meta: { successMessage: 'Jobs archived' },
    onSettled: () => invalidateFor('job.archive', qc),
  });
}

export function useAddTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, userId }: { jobId: number; userId: number }) =>
      jobApiClient.addTeamMember(jobId, { userId }),
    meta: { suppressErrorToast: true },
    onSettled: (_data, _err, { jobId }) =>
      invalidateFor('job.team.change', qc, { jobId }),
  });
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, userId }: { jobId: number; userId: number }) =>
      jobApiClient.removeTeamMember(jobId, userId),
    onSettled: (_data, _err, { jobId }) =>
      invalidateFor('job.team.change', qc, { jobId }),
  });
}

export function useSyncCtcJobMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobNumber }: { jobId: number; jobNumber: string }) =>
      ctcApiClient.syncCTCJobMetadata(jobNumber),
    meta: { successMessage: 'CTC Details Synced!' },
    onSettled: (_data, _err, { jobId, jobNumber }) =>
      invalidateFor('job.syncCtc', qc, { jobId, jobNumber }),
  });
}
